/**
 * S-K Screener — the daily unit of work, and the only screen in the section with a badge.
 *
 * ## 🔴 The verdict is the organising principle, not a column
 *
 * `API-and-UI.md` records the revision and the reason: the first version of this screen was a
 * ranked table with a percentile column, and the operator's objection was that *it is not clear
 * what to buy and what to sell*. Sorting by score and letting the reader infer the conclusion is a
 * **P1 violation** — the conclusion comes first, in the largest type. So names are grouped under
 * their verdict and the ranking only orders names *within* a group.
 *
 * That is also what makes the verdict legible without colour. Membership of a section is the
 * primary carrier; the header's type weight is the second; the spelled-out word is the third; the
 * drawn glyph in `glyphs.tsx` is the fourth. `BUY` is not green and `SELL` is not red, because
 * green and red are P&L and the `SELL` row here carries a real `−12.0%` beside the verdict.
 *
 * ## A row expands in place; it does not navigate
 *
 * The expansion is the evidence — the eight rules with their measured values — and reading it is
 * part of deciding, not a detour from it. Navigating would lose the other three names.
 *
 * ⚠ **`AGAINST IT` is never collapsed and never below the fold of the expansion.** It is the
 * refutation pass's output, and a `BUY` read without it is the exact failure the refutation pass
 * exists to prevent. It renders inside the same inset panel as the rules, directly beneath them.
 *
 * `HOLD` is collapsed by default — twelve unchanged names are not why the screen was opened — and
 * carries its count, so the collapse can never be mistaken for absence.
 */

import { useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Num } from '../../design/components/data/Num.jsx';
import { useTimestampMode, MODE_LABELS, clockOf } from '../../ui/timestamps';
import { asPercent } from './format';
import { CautionMark, Chevron, RuleMark, TierMark, VerdictMark } from './glyphs';
import type { CoverageTier, Screen, ScreenerRow, ScreenerRun, Verdict } from './model';

/**
 * The tier filter's options, and they are the whole filter set this screen ships.
 *
 * `API-and-UI.md` also asks for exchange, sector, watchlist and family. Those are absent rather
 * than drawn as chips that do nothing: `/stocks/companies` carries the fields and the screener row
 * does not, so a `Sector` chip here could only be a control with no predicate behind it. The
 * footnote at the foot of the screen names the four, which is the same choice `StrategiesScreen`
 * makes about its four missing columns.
 */
const TIERS: readonly (CoverageTier | 'ALL')[] = ['ALL', 'FULL', 'PRICE', 'LISTED'];

/**
 * The two screens, as tabs.
 *
 * 🔴 **Separate tabs, never one merged list.** `r1` and `g1` rank different things over different
 * universes and are graded as separate cohorts, so their scores are not comparable — putting a 78
 * from one beside a 78 from the other in a single ordering would be the cross-tier mis-rating
 * `Signals-and-Ranking.md` already refuses, one level up.
 */
function RulesetTabs({
  ruleset,
  onRuleset,
}: {
  ruleset: Screen;
  onRuleset: (next: Screen) => void;
}): JSX.Element {
  return (
    <div className="jmt-stk-tabs" role="tablist" aria-label="Which screen">
      {(
        [
          ['growth', 'Growth', 'smaller companies compounding fastest'],
          ['value', 'Value', 'cheap against their own history'],
        ] as const
      ).map(([key, label, hint]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={ruleset === key}
          title={hint}
          className={`jmt-stk-tab${ruleset === key ? ' jmt-stk-tab--on' : ''}`}
          onClick={() => onRuleset(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** `+62%`, `-4%`, or an em dash. Growth figures are fractions from the API. */
function pct(value: number | undefined, digits = 0): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(digits)}%`;
}

/**
 * Which direction class a reported figure takes — 🔴 **and it was unconditionally `--up` before.**
 *
 * Both growth cells hardcoded `jmt-stk-grow__big--up`, which is `var(--profit)`. So a negative
 * figure rendered its own minus sign **in profit green**: `-12 pts` in the colour that means
 * profit. That breaks colour law 2 at the top of `app.css` — *"`--profit` / `--loss` mean profit and
 * loss. Not 'good' and 'bad'"* — by using green to mean "this is the good column".
 *
 * ⚠ **`relativeStrength` is the one that actually goes negative in production.** `momentum` is an
 * optional family for `g1` and renormalises when absent, so a name can carry a `BUY` while
 * underperforming its universe over twelve weeks. `revenueYoy` is far less likely to be negative on
 * a growth `BUY` and is handled the same way regardless: a sign the reader can see must never
 * disagree with the colour it is painted in.
 *
 * ⚠ Nothing for zero or an unmeasurable figure — no class, so it inherits the neutral text colour.
 * An em dash painted green or red would be a direction where there is no measurement.
 */
function direction(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value) || value === 0) return '';
  return value > 0 ? 'jmt-stk-grow__big--up' : 'jmt-stk-grow__big--down';
}

/**
 * Where a reading sits, as five bands and a sixth state that is not a band.
 *
 * 🔴 **Thresholds from the Claude Design comp, and they match the measured distribution.** 0.2 and
 * 0.6 are not round numbers picked for tidiness: across 4,889 readings about two thirds fall inside
 * ±0.2, and only fourteen companies of 2,851 sit beyond +0.6. So `quiet` is what a normal row looks
 * like and any bar at all is already unusual — which is the property that makes the column scannable.
 *
 * ⚠ `level -1` is **not measured**, and it is deliberately not a band. It draws as a dashed axis
 * with no marker at all, because an empty axis cannot be misread as a centred one.
 */
export function band(value: number | undefined): { word: string; level: -1 | 0 | 1 | 2 } {
  if (value === undefined || !Number.isFinite(value)) return { word: 'not measured', level: -1 };
  const size = Math.abs(value);
  if (size < 0.2) return { word: 'quiet', level: 0 };
  if (value > 0) return size >= 0.6 ? { word: 'bought hard', level: 2 } : { word: 'bought', level: 1 };
  return size >= 0.6 ? { word: 'sold hard', level: 2 } : { word: 'sold', level: 1 };
}

/** How the rows are laid out. `rank` is the order the run published; `pressure` is a view over it. */
export type Order = 'rank' | 'pressure';

/**
 * Re-order a verdict group by the daily reading, strongest buying first.
 *
 * 🔴 **This is a view, not a re-ranking, and the distinction is load-bearing.** `rank` is the
 * frozen run's own ordering — it is what the scorecard grades and what `_strength` published, so
 * nothing here may change it. The row keeps rendering its `#N`, which is what makes the difference
 * visible: under this order the numbers go out of sequence, and that IS the message. A silent
 * re-numbering would put a rank beside a name that the run never gave it.
 *
 * 🔴 **Why it exists.** Measured on 2026-08-16 against the two most recent `r1` runs: the top
 * nineteen BUYs were the same nineteen names in the same order, two days apart, and the only
 * movement in the top twenty was one name shifting a single place. That is not a bug — `score` is
 * built from valuation percentiles, margins and balance-sheet quality, all of which come from
 * accounts filed once a quarter. But it means the published order cannot answer *what should I
 * look at today*, because its answer is the same every day until someone files.
 *
 * The reading at least moves. Of the 385 names passing all eight rules, 74 are being bought and 31
 * sold, and it is close to independent of score: the most-bought name sits at rank 188, while four
 * of the top ten by score are being sold.
 *
 * 🔴 **It moves, and it does not predict — measured, and the UI says so.** Recomputing this metric
 * over every stored bar and testing it against the following month's return, on the liquid universe
 * the screen actually shows (the R5 turnover floor), the rank correlation is **−0.015 over 21,498
 * observations** in eight non-overlapping monthly cohorts to June 2026. The `bought` band beat the
 * `quiet` middle in three of those eight, by margins indistinguishable from noise.
 *
 * ⚠ **The extreme bands are an illiquidity artifact, not conviction.** Median 20-day turnover is
 * €9,758 in `bought hard` and €33,454 in `sold hard`, against €1,083,506 in `quiet`. On a book that
 * thin a handful of trades set every close, so the ratio pins to ±1. Applying the R5 floor leaves
 * eight observations in `bought hard` out of fifty-nine — which is also why no name on the screener
 * has ever drawn a cap: the rows that qualify are exactly the rows too liquid to produce one.
 *
 * So this orders the reading list; it is not a ranking by expected return, and `SortNote` states
 * that on screen whenever it is active. See F-D107 in the Decision Log.
 *
 * ⚠ **An absent reading sorts last, never as zero.** Zero is *balanced* — a real measurement, and
 * the most common one. Sorting the unmeasured into the middle of the quiet names would file "we
 * could not measure this" under "buying and selling were even".
 *
 * ⚠ Ties keep their published order: `Array.prototype.sort` is stable, so equal readings fall back
 * to rank without a second comparator.
 */
export function byPressure(rows: readonly ScreenerRow[]): ScreenerRow[] {
  const reading = (row: ScreenerRow): number | undefined =>
    typeof row.accumulation === 'number' && Number.isFinite(row.accumulation)
      ? row.accumulation
      : undefined;
  return [...rows].sort((a, b) => {
    const left = reading(a);
    const right = reading(b);
    if (left === undefined) return right === undefined ? 0 : 1;
    if (right === undefined) return -1;
    return right - left;
  });
}

/**
 * The pressure tape: a bar above the axis for buying, below it for selling.
 *
 * 🔴 **Length is banded, not linear — three widths, so 0.37 and 0.41 draw identically.** That is the
 * honesty constraint made structural rather than promised: this is inferred from price and volume,
 * day-to-day differences inside a band are noise, and a linear bar would invite reading them.
 *
 * 🔴 **Direction is position, never hue.** Above the axis or below it, plus an end cap at the
 * extremes. `--profit`/`--loss` mean profit and loss and `--attn` means money at risk; buying
 * pressure is neither, so it spends no colour at all and stays legible in a monochrome screenshot.
 *
 * ⚠ Geometry is the comp's, at `half = 50`: a 100×26 box, axis at 12.5, bar 5px, cap 2×11. The
 * widths are `round(frac × 44)` for `frac` of 0.14 / 0.55 / 0.92 — 6px, 24px, 40px.
 */
function Tape({ value }: { value: number | undefined }): JSX.Element {
  const { level } = band(value);
  if (level < 0) {
    // No marker at all. The axis alone, dashed, so absence has a shape.
    return <span aria-hidden="true" className="jmt-stk-tape" data-level="none" />;
  }
  const fraction = level === 0 ? 0.14 : level === 1 ? 0.55 : 0.92;
  const width = Math.round(fraction * 44);
  const up = (value ?? 0) > 0;
  const style: Record<string, string> = {
    '--tape-bar-w': `${width}px`,
    '--tape-bar-left': `${up ? 50 : 50 - width}px`,
    '--tape-bar-top': up ? '7px' : '15px',
    '--tape-cap-left': `${up ? 50 + width - 2 : 50 - width}px`,
    '--tape-cap-top': up ? '3px' : '13px',
  };
  return (
    <span
      aria-hidden="true"
      className="jmt-stk-tape"
      data-level={String(level)}
      style={style as never}
    />
  );
}

/** `$1.3bn`, `$934m`. ⚠ Never rounded to zero — a small cap is the point of this screen. */
function cap(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}bn`;
  return `$${Math.round(value / 1e6)}m`;
}

/**
 * One growth row: how small, how fast, is the market agreeing.
 *
 * 🔴 **No red/green risk gauge here, deliberately.** For this screen volatility is the price of the
 * opportunity rather than a warning, and a red bar beside the best candidate teaches the wrong
 * reflex. The one fact that genuinely changes how the row is read — burning cash — is an `--attn`
 * chip, which is the colour law for *money at risk*. Green stays P&L direction only.
 */
function GrowthRow({ row }: { row: ScreenerRow }): JSX.Element {
  const speed =
    row.revenueAcceleration === undefined
      ? 'trend not measurable'
      : row.revenueAcceleration > 0.05
        ? 'and speeding up'
        : row.revenueAcceleration < -0.05
          ? 'but slowing'
          : 'holding steady';
  return (
    <Link className="jmt-stk-grow" to={`/screener/${row.isin}`}>
      <span className="jmt-stk-grow__rank">#{row.rank ?? '—'}</span>
      <span className="jmt-stk-grow__id">
        <span className="jmt-stk-grow__name">
          {row.name}
          {row.profitable === false ? (
            <span className="jmt-stk-grow__chip">NOT YET PROFITABLE</span>
          ) : null}
        </span>
        <span className="jmt-stk-grow__tick">{row.ticker}</span>
      </span>
      <span className="jmt-stk-grow__cell">
        <span className="jmt-stk-grow__big">{cap(row.marketCap)}</span>
        <span className="jmt-stk-grow__label">MARKET CAP</span>
      </span>
      <span className="jmt-stk-grow__cell">
        <span className={`jmt-stk-grow__big ${direction(row.revenueYoy)}`}>
          {pct(row.revenueYoy)}
        </span>
        <span className="jmt-stk-grow__sub">over the year, {speed}</span>
      </span>
      <span className="jmt-stk-grow__cell">
        <span className={`jmt-stk-grow__big ${direction(row.relativeStrength)}`}>
          {row.relativeStrength === undefined
            ? '—'
            : `${row.relativeStrength >= 0 ? '+' : ''}${Math.round(row.relativeStrength * 100)} pts`}
        </span>
        <span className="jmt-stk-grow__sub">vs the market, 12 weeks</span>
      </span>
      <span className="jmt-stk-grow__score">
        <span className="jmt-stk-grow__num">{row.score ?? '—'}</span>
        <span className="jmt-stk-grow__bar">
          <span className="jmt-stk-grow__fill" style={{ width: `${row.score ?? 0}%` }} />
        </span>
      </span>
      {row.summary ? <span className="jmt-stk-grow__say">{row.summary}</span> : null}
      {/* 🔴 The counter-case is never collapsed and never below the fold — the refutation pass
          exists because a BUY read without it is the failure the whole apparatus prevents. It is
          rendered whenever it is present, and its absence is silence rather than a claim. */}
      {row.against ? (
        <span className="jmt-stk-grow__against">
          <span className="jmt-stk-grow__against-label">AGAINST</span>
          {row.against}
        </span>
      ) : null}
    </Link>
  );
}

export function ScreenerScreen({
  run,
  ruleset = 'value',
  onRuleset,
}: {
  run: ScreenerRun;
  ruleset?: Screen;
  onRuleset?: (next: Screen) => void;
}): JSX.Element {
  const mode = useTimestampMode();
  const [tier, setTier] = useState<CoverageTier | 'ALL'>('ALL');
  const [heldOnly, setHeldOnly] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  // 🔴 Defaults to `rank`. The published order is the run's own claim and the one the scorecard
  // grades; the reader has to ask for the other view, which is the right way round.
  const [order, setOrder] = useState<Order>('rank');

  const keep = useMemo(
    () => (rows: ScreenerRow[]) => {
      const kept = rows.filter(
        (row) => (tier === 'ALL' || row.tier === tier) && (!heldOnly || row.held),
      );
      return order === 'pressure' ? byPressure(kept) : kept;
    },
    [tier, heldOnly, order],
  );

  const buy = keep(run.buy);
  const sell = keep(run.sell);
  const review = keep(run.review);

  // 🔴 **The badge counts `BUY + SELL + REVIEW` and never `HOLD`.** `HOLD` is the absence of a
  // claim, and including it would make the badge read *high* on the calmest days — which is the
  // one reading that would teach the operator to ignore it.
  const actionable = buy.length + sell.length + review.length;

  return (
    <div className="jmt-screen jmt-screen--capped">
      {onRuleset ? <RulesetTabs ruleset={ruleset} onRuleset={onRuleset} /> : null}
      <header className="jmt-stk-head">
        <p className="jmt-stk-head__hero">
          <Num value={actionable} kind="plain" step="xl" dp={0} />
          <span className="jmt-stk-head__label">need a decision</span>
        </p>
        <p className="jmt-stk-head__counts">
          <span>
            <b>{buy.length}</b> buy
          </span>
          <span>
            <b>{sell.length}</b> sell
          </span>
          <span>
            <b>{review.length}</b> review
          </span>
          <span data-quiet>
            <b>{run.hold.length}</b> hold
          </span>
          <span data-quiet>
            <b>
              <Num value={run.screened} kind="plain" dp={0} />
            </b>{' '}
            screened
          </span>
        </p>
      </header>

      <div className="jmt-stk-filters">
        <span className="jmt-stk-filters__label">Filter</span>
        {TIERS.map((option) => (
          <button
            key={option}
            type="button"
            className="jmt-stk-chip"
            data-on={tier === option ? '' : undefined}
            aria-pressed={tier === option}
            onClick={() => setTier(option)}
          >
            {option === 'ALL' ? 'All tiers' : option}
          </button>
        ))}
        <button
          type="button"
          className="jmt-stk-chip"
          data-on={heldOnly ? '' : undefined}
          aria-pressed={heldOnly}
          onClick={() => setHeldOnly(!heldOnly)}
          title="Only names the operator holds at the broker"
        >
          Held
        </button>
        {/* 🔴 A separate question from the filters beside it — *which rows* versus *in what order* —
            and the only control on this screen that touches the daily signal. `Rank` is the order
            the run published and stays the default; `Being bought` re-reads the same rows by the
            tape, which is the one thing that moves between filings. The rank badges go out of
            sequence under it, deliberately: the published order is unchanged and the row says so. */}
        <span className="jmt-stk-filters__label jmt-stk-filters__label--sep">Order</span>
        <button
          type="button"
          className="jmt-stk-chip"
          data-on={order === 'rank' ? '' : undefined}
          aria-pressed={order === 'rank'}
          onClick={() => setOrder('rank')}
          title="The order the run published — ranked on the score"
        >
          Rank
        </button>
        <button
          type="button"
          className="jmt-stk-chip"
          data-on={order === 'pressure' ? '' : undefined}
          aria-pressed={order === 'pressure'}
          onClick={() => setOrder('pressure')}
          title="Strongest buying first — an inference from price and volume, with no measured forward edge"
        >
          Being bought
        </button>
        {/* The run's identity, and it is here rather than in a footer because a verdict is only
            explicable against the ruleset that produced it. `run_id` is printed in full: it is
            what `GET /stocks/screen/{run_id}` is asked for when a past screen has to be
            reproduced byte-for-byte. */}
        <span className="jmt-stk-filters__run">
          run {clockOf(run.ranAt, mode)} {MODE_LABELS[mode]} · ruleset {run.rulesetVersion} ·{' '}
          <code>{run.runId}</code>
        </span>
      </div>

      {order === 'pressure' ? <SortNote /> : null}

      <VerdictBand verdict="BUY" rows={buy} />
      <VerdictBand verdict="SELL" rows={sell} />
      <VerdictBand verdict="REVIEW" rows={review} />

      <div>
        <button
          type="button"
          className="jmt-stk-band jmt-stk-band--button"
          onClick={() => setHoldOpen(!holdOpen)}
          aria-expanded={holdOpen}
        >
          <VerdictMark verdict="HOLD" />
          <span className="jmt-stk-band__word">HOLD</span>
          <span className="jmt-stk-band__n">({run.hold.length})</span>
          <span className="jmt-stk-band__rule" data-quiet />
          <span className="jmt-stk-band__state">{holdOpen ? 'expanded' : 'collapsed'}</span>
          <span className="jmt-stk-chev" data-open={holdOpen ? '' : undefined}>
            <Chevron />
          </span>
        </button>
        {holdOpen ? (
          <div className="jmt-panel jmt-stk-holds">
            {run.hold.map((item) => (
              <div className="jmt-stk-hold" key={item.ticker}>
                <span className="jmt-stk-hold__t">{item.ticker}</span>
                <span className="jmt-stk-hold__n">{item.name}</span>
                <span className="jmt-stk-hold__r">{item.note}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

    </div>
  );
}

/**
 * What the pressure ordering is measured to be worth, stated where it is being used.
 *
 * 🔴 **A control that reorders a list is a claim by placement, and this one would be the wrong
 * claim left silent.** Putting the most-bought name at the top of a BUY list reads as *look here
 * first*, and the backtest says there is no reason to: rank correlation −0.015 against the next
 * month's return, over 21,498 liquid readings. The note is not a disclaimer bolted on — it is the
 * measurement, printed beside the thing it measures, so the ordering cannot quietly become a
 * recommendation the evidence does not support.
 *
 * ⚠ Rendered only while the ordering is active. A permanent footnote would be scenery within a
 * week, and it has nothing to say about the published rank.
 */
function SortNote(): JSX.Element {
  return (
    <p className="jmt-stk-sortnote">
      Ordered by the tape, not by expected return. Tested against the following month over eight
      monthly cohorts to June 2026: buying pressure and the next month&rsquo;s return rank together
      at <b>−0.02</b> across 21,498 readings — no edge in either direction. It says what has been
      bought, never what will rise.
    </p>
  );
}

/** One verdict's heading and its panel. Renders nothing at all when the group is empty. */
function VerdictBand({ verdict, rows }: { verdict: Verdict; rows: ScreenerRow[] }): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div className="jmt-stk-group">
      <div className="jmt-stk-band">
        <VerdictMark verdict={verdict} />
        <span className="jmt-stk-band__word">{verdict}</span>
        <span className="jmt-stk-band__n">({rows.length})</span>
        <span className="jmt-stk-band__rule" />
      </div>
      <div className="jmt-panel jmt-panel--flush jmt-stk-rows">
        {rows.map((row) =>
          // ⚠ The row's SHAPE decides its layout: a `g1` row carries `marketCap` and no `rules`,
          // so no ruleset flag has to be threaded down here to keep the two renderings apart.
          row.marketCap === undefined ? (
            <Row key={row.isin} row={row} />
          ) : (
            <GrowthRow key={row.isin} row={row} />
          ),
        )}
      </div>
    </div>
  );
}

function Row({ row }: { row: ScreenerRow }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const passed = row.rules.filter((rule) => rule.passed).length;
  const dossier = `/screener/${row.isin}`;

  return (
    <div className="jmt-stk-row" data-held={row.held ? '' : undefined}>
      {/* A `<button>` wrapping the whole summary, so the expansion is keyboard-reachable and
          announces its own state. The dossier link sits *outside* it: a control inside a control
          is not focusable in order, and this one navigates while its parent expands. */}
      <button
        type="button"
        className="jmt-stk-row__summary"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {/* 🔴 The rank sits before the ticker, because it is the first thing that matters when a
            section holds 219 names: it says where to stop reading. ⚠ Rendered only when the run
            ordered this row — an absent rank is not rank 0, which would read as "first". */}
        {/* ⚠ Always rendered, empty when the row has no rank, so the grid keeps three children.
            Conditionally omitting it made the body land in an unsized implicit column. */}
        <span className="jmt-stk-row__rank">{row.rank ? `#${row.rank}` : ''}</span>
        <span className="jmt-stk-row__t">{row.ticker}</span>
        <span className="jmt-stk-row__body">
          <span className="jmt-stk-row__head">
            <span className="jmt-stk-row__name">{row.name}</span>
            {/* 🔴 The rating out of 100, and it sits beside the name rather than beside the rank
                because it is a property of the company, not of its position in a list.

                ⚠ **Rendered only inside a verdict section, which is where it is meaningful.** It is
                `_strength` — the value the run sorted on — so it is monotone with the order above
                and below it by construction. Measured across all 207 BUYs before it went on screen:
                45 to 92, median 75, 40 distinct values. The four-mark family bars failed that same
                test and came off the row. */}
            {row.score !== undefined ? (
              <Gauge
                kind="score"
                value={row.score}
                title="rating out of 100, within this verdict group"
              />
            ) : null}
            {/* ⚠ An em dash, not an empty cell. `price` is nullable — `store.money()` returns
                None when the amount is unknown, and a blank looked like a layout fault. */}
            <span className="jmt-stk-row__price">{row.price ?? '—'}</span>
            {/* ⚠ Rendered whenever it is a number, INCLUDING 0. A quiet, liquid, fully-covered
                name genuinely scores 0 and that is a measurement; `undefined` is the one that
                means the market could not be measured, and it renders nothing at all. */}
            {row.risk !== undefined ? (
              <Gauge
                kind="risk"
                value={row.risk}
                title={
                  row.riskNotes?.length
                    ? `risk ${row.risk}/100 — ${row.riskNotes.join(' · ')}`
                    : 'how much this moves, out of 100 — never which way'
                }
              />
            ) : null}
            {row.held ? (
              <span className="jmt-stk-row__held">
                you hold
                <Num value={asPercent(row.held.changePct)} kind="percent" dp={1} arrow step="sm" />
              </span>
            ) : null}
            {row.failedRule ? (
              <span className="jmt-stk-count jmt-stk-count--rule">
                <RuleMark passed={false} size={11} />
                {row.failedRule}
              </span>
            ) : passed < row.rules.length ? (
              /* ⚠ The count is shown ONLY when it carries information — some rule did not pass or
                 abstained. `8/8` beside `passes all 8 rules` was the same fact twice, in digits
                 and in words, and the operator's objection was *"right now I just see numbers"*.
                 A count that appears only when it is not full makes its appearance meaningful. */
              <span className="jmt-stk-count" title="rules passed of rules evaluated">
                {passed}/{row.rules.length}
              </span>
            ) : null}
            <TierMark tier={row.tier} />
          </span>
          {/* 🔴 The highlights come FIRST, above the rule reason, and that ordering is the whole
              point of the 2026-08-11 revision.

              Looked at in the running UI, every one of 216 BUY rows read `8/8`, `passes all 8
              rules`, and the same two family words in a different order — so the leading line was
              identical on rank 1 and rank 216 and said nothing about either company. `reason` is a
              statement about the *ruleset* ("passes all 8 rules"); a highlight is a statement about
              *this company* ("cheaper than 95% of its own EV/sales history over 20 quarters"). The
              specific one goes first because it is the one that distinguishes.

              ⚠ `reason` is kept, not replaced. On a SELL or a REVIEW it names the rule that
              carried the verdict, which is the most important line on the row — and on a BUY it is
              the audit trail for why the name is here at all. It moves below, it does not go. */}
          {row.highlights?.length ? (
            <span className="jmt-stk-why">
              {row.highlights.map((item) => (
                <span className="jmt-stk-why__item" key={item}>
                  {item}
                </span>
              ))}
            </span>
          ) : null}
          {/* 🔴 What the company HAS reported, past tense, on its own line.
              Asked for as "expected growth in 1 month or 1 year". The expected version needs
              consensus analyst estimates, which `Universe-and-Data.md` records as the one data
              family no free source provides — a purchase, not an algorithm. This is the realised
              trajectory instead, and it is a fact rather than a claim about the future. */}
          {row.growth?.length ? (
            <span className="jmt-stk-growth">
              <span className="jmt-stk-growth__label">REPORTED</span>
              {row.growth.map((item) => (
                <span className="jmt-stk-growth__item" key={item}>
                  {item}
                </span>
              ))}
            </span>
          ) : null}
          <span className="jmt-stk-row__reason">{row.reason}</span>
          {/* 🔴 The only line on this card that is not read off the frozen run, and the only one
              that changes daily. It is an INFERENCE — nobody filed it — so it is hedged in words
              and dated, and it sits BELOW the reason: the reason is why the company qualified, this
              is only what the tape has been doing since. Absent renders nothing rather than a dash,
              because an unmeasured stock has no reading to report. */}
          {row.accumulation !== undefined ? (
            <span className="jmt-stk-row__flow">
              <Tape value={row.accumulation} />
              <span className="jmt-stk-row__flow-word" data-level={String(band(row.accumulation).level)}>
                {band(row.accumulation).word}
              </span>
              {/* ⚠ The hedge stays in type, beside a mark that carries no number. A filed document
                  gets a citation; this gets the words that say where it came from. */}
              <span className="jmt-stk-row__flow-note">
                20-day, from price and volume
                {row.accumulationAsOf ? ` · to ${row.accumulationAsOf.slice(0, 10)}` : ''}
              </span>
            </span>
          ) : null}
          {/* Why it ranks where it does, from the stored sub-scores. ⚠ Beneath the reason, not
              instead of it: the reason is why it qualified, this is why it sorted here, and
              collapsing the two would let an ordering argument read as a verdict. */}
          {row.why ? <span className="jmt-stk-row__why">{row.why}</span> : null}
          {/* 🔴 **No family bars on the row, and that was decided by measuring the rendered list.**
              They were here first. Across 32 rendered rows they produced **four distinct patterns,
              one of which covered twenty-six of them** — because a BUY is by construction a name
              that scored well on every family, so the sub-scores barely vary across the group.
              Neither `ceil` nor `round` fixed it; the compression is in the data.

              They also duplicated `why` directly above, which already names the top families in
              words. A widget that repeats the line above it and cannot tell rank 1 from rank 26 is
              weight on a card whose whole problem was that nothing on it discriminated. The
              highlights do: 21 distinct opening facts across the same 32 rows.

              ⚠ `FamilyBars` is kept and used on the Company page, where there is no comparison to
              make and a weak or unmeasured family is worth seeing. */}
          {/* 🔴 The dossier's paragraph, on the three the composite shortlisted. Uncollapsed and
              above the fold, for the same reason `AGAINST IT` is: a reader who has to click to see
              the counter-case is a reader who will not. Its own last clause is the counter-case. */}
          {/* ⚠ `jmt-stk-para`, not `…row__summary`: that class is the wrapping button, and two
              selectors one hyphen apart in the same subtree is a debugging session waiting. */}
          {row.summary ? <span className="jmt-stk-para">{row.summary}</span> : null}
          {/* 🔴 `AGAINST IT` on the row, not only inside the expansion.
              `API-and-UI.md` already required it never be *collapsed* within the expansion; the
              operator asked for it on the overview, which is the stronger version of the same
              argument — a BUY read without its counter-case is the failure the refutation pass
              exists to prevent, and a reader who must click to see it will not.

              ⚠ Only where there is one. The `(none recorded)` phrasing stays inside the expansion:
              on a list of 30 rows it would be thirty lines of absence, which trains the eye to skip
              the line that matters on the day it is filled. */}
          {row.against ? (
            <span className="jmt-stk-row__against">
              <span className="jmt-stk-row__against-label">AGAINST</span>
              {row.against}
            </span>
          ) : null}
          {row.caution ? (
            <span className="jmt-stk-caution">
              <CautionMark />
              <span>{row.caution}</span>
            </span>
          ) : null}
          {/* The dotted marker travels with the row, not only with the header pill: on a phone
              the header scrolls away and the row is all that is left. */}
          {row.held ? (
            <span className="jmt-stk-broker jmt-stk-row__broker">
              <span aria-hidden="true" className="jmt-stk-broker__mark" />
              BROKER · READ-ONLY
            </span>
          ) : null}
        </span>
        <span className="jmt-stk-chev" data-open={open ? '' : undefined}>
          <Chevron />
        </span>
      </button>
      <button
        type="button"
        className="jmt-stk-row__dossier"
        onClick={() => navigate(dossier)}
        title="Open the full dossier"
      >
        <span className="jmt-stk-row__dossier-word">Dossier</span>
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12h13M13 6l6 6-6 6" />
        </svg>
      </button>

      {open ? (
        <div className="jmt-stk-row__open">
          <div className="jmt-stk-evidence">
            {row.rules.map((rule) => (
              <div className="jmt-stk-rule" key={rule.id}>
                <span className="jmt-stk-rule__id">{rule.id}</span>
                <span className="jmt-stk-rule__pair">
                  <span className="jmt-stk-rule__name">{rule.name}</span>
                  <span className="jmt-stk-rule__reading">{rule.reading}</span>
                </span>
                <span className="jmt-stk-rule__mark">
                  <RuleMark passed={rule.passed} />
                </span>
              </div>
            ))}
            <div className="jmt-stk-against">
              <p className="jmt-stk-against__label">AGAINST IT</p>
              <p className="jmt-stk-against__text">{row.against}</p>
            </div>
          </div>
          <div className="jmt-stk-row__actions">
            <button type="button" className="jmt-stk-primary" onClick={() => navigate(dossier)}>
              Open the full dossier
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h13M13 6l6 6-6 6" />
              </svg>
            </button>
            <span className="jmt-stk-row__actions-note">
              bull and bear, refutation pass, falsifiers
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}


/**
 * A 0-100 figure as a coloured bar and a number. Two kinds, and they run in opposite directions.
 *
 * 🔴 **This spends `--profit`, `--loss` and `--attn` on something that is not P&L or money at
 * risk, which the three colour laws forbid.** The operator was shown the collision — an ASML row
 * reading a red `26` beside a green `+6.0%` held return, two colours meaning opposite things a
 * hundred pixels apart — and chose the coloured bar anyway. That is their call and this is it.
 *
 * ⚠ **One thing is held back rather than argued about again: the NUMBER stays in the text ramp and
 * only the BAR takes colour.** The collision that actually misleads is two coloured *numerals* in
 * one row, because `Num` renders P&L as a coloured numeral. A coloured bar beside a plain numeral
 * is distinguishable at a glance; two coloured numerals are not.
 *
 * ⚠ `score` is high-is-good and `risk` is high-is-bad, so the ramps are mirrored. They are never
 * placed adjacent for that reason — the price sits between them.
 */
function Gauge({
  kind,
  value,
  title,
}: {
  kind: 'score' | 'risk';
  value: number;
  title: string;
}): JSX.Element {
  // Four bands rather than a continuous ramp: a continuous hue implies the underlying figure is
  // precise to the point, and neither of these is.
  const band = value >= 75 ? 'a' : value >= 50 ? 'b' : value >= 25 ? 'c' : 'd';
  return (
    <span className="jmt-stk-gauge" data-kind={kind} data-band={band} title={title}>
      <span className="jmt-stk-gauge__track" aria-hidden="true">
        <span className="jmt-stk-gauge__fill" style={{ width: `${Math.max(2, value)}%` }} />
      </span>
      <b className="jmt-stk-gauge__n">{value}</b>
      <span className="jmt-stk-gauge__of">{kind === 'risk' ? 'risk' : '/100'}</span>
    </span>
  );
}
